import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, RefreshCw, Download, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  company: "",
  job_title: "",
  email: "",
  phone: "",
  captcha_answer: "",
};

export default function BrochureModal({ open, onOpenChange }) {
  const [form, setForm] = useState(empty);
  const [captcha, setCaptcha] = useState({ id: "", question: "" });
  const [loading, setLoading] = useState(false);

  const fetchCaptcha = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/captcha`);
      setCaptcha(res.data);
      setForm((f) => ({ ...f, captcha_answer: "" }));
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.error("captcha fetch failed", e);
      }
    }
  }, []);

  useEffect(() => {
    if (open) fetchCaptcha();
  }, [open, fetchCaptcha]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!form.name || !form.company || !form.job_title || !form.email || !form.phone) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!form.captcha_answer) {
      toast.error("Please answer the captcha.");
      return;
    }

    setLoading(true);
    try {
      const payload = { ...form, captcha_question_id: captcha.id };
      const res = await axios.post(`${API}/brochure-request`, payload);
      const url = res.data?.brochure_url;
      toast.success("Thanks! Your brochure is downloading now.");
      if (url) {
        // Open the PDF in a new tab (avoids browser popup blocking better than window.open sync)
        window.open(url, "_blank", "noopener,noreferrer");
      }
      setForm(empty);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(String(detail));
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-11 rounded-md bg-[#050d1a] border border-cyan-900/60 px-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30 transition-colors";

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="brochure-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div className="relative w-full max-w-lg rounded-lg border border-cyan-900/60 bg-[#0B1120] shadow-[0_20px_60px_-20px_rgba(0,240,255,0.35)]">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          data-testid="brochure-modal-close"
          className="absolute right-3 top-3 h-9 w-9 inline-flex items-center justify-center rounded-md border border-cyan-500/30 text-cyan-200 hover:bg-cyan-400/10"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 lg:p-8">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md border border-cyan-500/40 bg-cyan-500/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-cyan-300">
                Event Brochure
              </div>
              <h3 className="font-display text-white font-semibold text-lg">
                Download the full brochure
              </h3>
            </div>
          </div>

          <p className="mt-4 text-sm text-slate-400">
            Please share your details below to receive the Philippines National
            Cyber Resilience Forum brochure. All fields are required.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="brochure-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Name*</span>
                <input data-testid="brochure-name" className={inputCls + " mt-1"} value={form.name} onChange={update("name")} placeholder="Full name" required />
              </label>
              <label className="block">
                <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Company Name*</span>
                <input data-testid="brochure-company" className={inputCls + " mt-1"} value={form.company} onChange={update("company")} placeholder="Company / Organisation" required />
              </label>
              <label className="block">
                <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Job Title*</span>
                <input data-testid="brochure-job-title" className={inputCls + " mt-1"} value={form.job_title} onChange={update("job_title")} placeholder="e.g. CISO" required />
              </label>
              <label className="block">
                <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Email*</span>
                <input data-testid="brochure-email" type="email" className={inputCls + " mt-1"} value={form.email} onChange={update("email")} placeholder="you@company.com" required />
              </label>
              <label className="block sm:col-span-2">
                <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Phone Number*</span>
                <input data-testid="brochure-phone" className={inputCls + " mt-1"} value={form.phone} onChange={update("phone")} placeholder="+63 ..." required />
              </label>
            </div>

            <div className="rounded-md border border-cyan-900/50 bg-[#050d1a] p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400">
                  Verify
                </div>
                <div className="mono text-cyan-200 text-sm truncate">
                  {captcha.question || "Loading..."}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="brochure-captcha-answer"
                  className="h-10 w-20 rounded-md bg-[#0B1120] border border-cyan-900/60 px-3 text-sm text-white focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30"
                  value={form.captcha_answer}
                  onChange={update("captcha_answer")}
                  placeholder="Answer"
                  inputMode="numeric"
                  required
                />
                <button
                  type="button"
                  data-testid="brochure-captcha-refresh"
                  onClick={fetchCaptcha}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-cyan-500/40 text-cyan-200 hover:bg-cyan-400/10"
                  aria-label="Refresh captcha"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="brochure-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 disabled:opacity-60 disabled:cursor-not-allowed text-slate-950 font-semibold px-6 py-3 transition-colors"
            >
              {loading ? "Preparing..." : "Get the Brochure"}
              <Download className="w-4 h-4" />
            </button>

            <p className="text-[11px] text-slate-500 mono uppercase tracking-widest">
              Your details are used only to send you the brochure and event updates.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
